export default function StickySidebarNav() {
  const header: HTMLElement | null = document.querySelector("header");
  const stickyScrollNav: HTMLDivElement | null = document.querySelector(".js-scroll-nav");
  const stickySidebarNav: HTMLDivElement | null = document.querySelector(".js-sticky-sidebar-nav");
  const rtfObserveElements = document.querySelectorAll(".rtf-observer");

  if (stickySidebarNav && header) {
    const scrollToBtn = stickySidebarNav.querySelectorAll<HTMLAnchorElement>(".js-scroll-to-btn");
    const mobileBtn: HTMLButtonElement | null = stickySidebarNav.querySelector(".js-mobile-btn");
    const mobileBtnIcon: HTMLSpanElement | null = stickySidebarNav.querySelector(".js-icon");
    const mobileContentWrapper: HTMLDivElement | null = stickySidebarNav.querySelector(".js-mobile-content-wrapper");
    const mobileContent: HTMLDivElement | null = stickySidebarNav.querySelector(".js-mobile-content");
    let isOpen = false;
    let isClickScrolling = false;
    let clickScrollUnlockTimeout: number | undefined;
    let settleScrollHandler: (() => void) | null = null;
    let clickedSectionId: string | null = null;

    const getScrollOffset = () => {
      const paddingOffset = 24;
      let offset = header.offsetHeight + paddingOffset;
      
      if (stickyScrollNav) {
        const rect = stickyScrollNav.getBoundingClientRect();
        const isScrollNavVisible = rect.top >= 0 && rect.height > 0;
        
        if (isScrollNavVisible) {
          offset += stickyScrollNav.offsetHeight;
        }
      }
      
      return offset;
    };

    const handleInit = () => {
      stickySidebarNav.style.top = `${getScrollOffset()}px`;
    };

    // Update position on scroll to account for scroll direction
    let rafId: number | null = null;
    const handleScrollPosition = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        // Update the top position based on current scroll state
        // getScrollOffset() checks if scroll nav is visible
        const newTop = getScrollOffset();
        stickySidebarNav.style.top = `${newTop}px`;
      });
    };

    const initRtfHeadings = () => {
      if (rtfObserveElements && rtfObserveElements.length > 0) {
        rtfObserveElements.forEach((element) => {
          if (element && element.childNodes) {
            Array.from(element.children).forEach((node) => {
              if (node.nodeType === 1 && /^H[1-6]$/.test(node.tagName)) {
                node.setAttribute("data-nav-item", "");
              }
            });
          }
        });
      }
    };

    const handleToggleMenu = () => {
      let maxHeight = 0;
      isOpen = !isOpen;

      if (mobileBtn && mobileContent && mobileContentWrapper && mobileBtnIcon) {
        mobileBtn.setAttribute("aria-expanded", `${isOpen}`);

        if (isOpen) {
          maxHeight += mobileContent.clientHeight;
          mobileContentWrapper.style.maxHeight = `${maxHeight}px`;
          mobileBtnIcon.style.transform = "rotate(180deg)";
        } else {
          mobileContentWrapper.style.maxHeight = "";
          mobileBtnIcon.style.transform = "rotate(0deg)";
        }
      }
    };

    const handleScrollTo = (id: string) => {
      const cleanId = id.startsWith("#") ? id.slice(1) : id;
      const section: HTMLElement | null = document.getElementById(cleanId);

      if (section) {
        const sectionOffset = section.getBoundingClientRect().top + window.scrollY;
        const scrollOffset = getScrollOffset();

        window.scrollTo({
          top: sectionOffset - scrollOffset,
          behavior: "smooth",
        });
      }
    };

    const handleResetActiveButtons = () => {
      if (scrollToBtn && scrollToBtn.length > 0) {
        scrollToBtn.forEach((btn) => {
          btn.setAttribute("data-active", "false");
        });
      }
    };

    const setActiveButton = (sectionId: string) => {
      const button = stickySidebarNav.querySelector(`[data-target-id="#${sectionId}"]`);
      if (button) {
        handleResetActiveButtons();
        button.setAttribute("data-active", "true");
      }
    };

   
    const getOrderedNavSections = () => {
      const sections: HTMLElement[] = [];

      scrollToBtn.forEach((btn) => {
        const targetId = btn.getAttribute("data-target-id");

        if (!targetId) {
          return;
        }

        const cleanId = targetId.startsWith("#") ? targetId.slice(1) : targetId;
        const section = document.getElementById(cleanId);

        if (section && !sections.includes(section)) {
          sections.push(section);
        }
      });

      return sections.sort((firstSection, secondSection) => {
        if (firstSection === secondSection) {
          return 0;
        }

        const secondFollowsFirst =
          firstSection.compareDocumentPosition(secondSection) & Node.DOCUMENT_POSITION_FOLLOWING;

        return secondFollowsFirst ? -1 : 1;
      });
    };

    const updateActiveSection = () => {
      if (isClickScrolling) return;

      const navSections = getOrderedNavSections();

      if (navSections.length === 0) {
        return;
      }

      const scrollOffset = getScrollOffset();
      let activeSectionId: string | null = null;

      for (const section of navSections) {
        if (!section.id) continue;

        if (section.getBoundingClientRect().top <= scrollOffset + 5) {
          activeSectionId = section.id;
        } else {
          break;
        }
      }

   
      if (clickedSectionId) {
        const clickedIndex = navSections.findIndex((section) => section.id === clickedSectionId);
        const activeIndex = activeSectionId
          ? navSections.findIndex((section) => section.id === activeSectionId)
          : -1;

        if (clickedIndex > -1 && activeIndex < clickedIndex) {
          setActiveButton(clickedSectionId);
          return;
        }

        clickedSectionId = null;
      }

      if (activeSectionId) {
        setActiveButton(activeSectionId);
      } else {
        handleResetActiveButtons();
      }
    };

    const stopWatchingForSettle = () => {
      if (settleScrollHandler) {
        window.removeEventListener("scroll", settleScrollHandler);
        settleScrollHandler = null;
      }

      window.clearTimeout(clickScrollUnlockTimeout);
    };

    const unlockClickScrollingAfterSettle = () => {
      stopWatchingForSettle();

      const finishClickScroll = () => {
        stopWatchingForSettle();
        isClickScrolling = false;
        updateActiveSection();
      };

      settleScrollHandler = () => {
        window.clearTimeout(clickScrollUnlockTimeout);
        clickScrollUnlockTimeout = window.setTimeout(finishClickScroll, 150);
      };

      window.addEventListener("scroll", settleScrollHandler, { passive: true });
      clickScrollUnlockTimeout = window.setTimeout(finishClickScroll, 1500);
    };

    const handleOnClick = (event: Event) => {
      const button = event.currentTarget as HTMLAnchorElement;
      const targetId = button.getAttribute("data-target-id");

      if (!targetId) {
        return;
      }

      event.preventDefault();
      isClickScrolling = true;
      clickedSectionId = targetId.startsWith("#") ? targetId.slice(1) : targetId;

      handleResetActiveButtons();
      button.setAttribute("data-active", "true");
      handleScrollTo(targetId);
      unlockClickScrollingAfterSettle();
    };

    let scrollTimeout: number | null = null;
    const handleScroll = () => {
      if (scrollTimeout) {
        cancelAnimationFrame(scrollTimeout);
      }
      scrollTimeout = requestAnimationFrame(() => {
        handleScrollPosition();
        updateActiveSection();
      });
    };

    if (scrollToBtn && scrollToBtn.length > 0) {
      scrollToBtn.forEach((btn) => {
        btn.addEventListener("click", handleOnClick);
      });
    }

    if (mobileBtn) {
      mobileBtn.addEventListener("click", handleToggleMenu);
    }

    handleInit();
    initRtfHeadings();
    
    setTimeout(() => {
      handleInit();
      updateActiveSection();
    }, 100);

    window.addEventListener("scroll", handleScroll, { passive: true });

    ["wheel", "touchstart", "keydown"].forEach((eventName) => {
      window.addEventListener(
        eventName,
        () => {
          clickedSectionId = null;
        },
        { passive: true },
      );
    });

    let resizeTimeout: number;
    const recalculatePosition = () => {
      // Use requestAnimationFrame to ensure layout has settled
      requestAnimationFrame(() => {
        const newTop = getScrollOffset();
        stickySidebarNav.style.top = `${newTop}px`;
      });
    };
    
    window.addEventListener("resize", () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = window.setTimeout(() => {
        recalculatePosition();
        updateActiveSection();
      }, 150);
    });
    
    // Listen for header resize events from mega-menu-desktop
    window.addEventListener('headerResize', recalculatePosition);
    
    // Also recalculate on page load to ensure correct initial position
    window.addEventListener('load', recalculatePosition);
  }
}
